import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import API from "../api/axiosConfig";
import { useUser } from "../context/UserContext";

/*
    This component is used to check user authentication and group status.

    When used, provide a requiredGroup value to check if the user belongs to
    that specific group name.
*/

// Passing "children" allow it to be used as a frontend wrapper component.
const PrivateRoute = ({ children, requiredGroup = "Default" }) => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useUser();
  const [isValidating, setIsValidating] = useState(true);

  useEffect(() => {
    // Wait until UserContext completes its initial auth check
    if (authLoading) return;

    // If no user is authenticated, redirect home
    if (!user) {
      navigate("/?alert=Must+be+logged+in+to+view+this+page", {
        replace: true,
      });
      return;
    }

    // Validate group permissions with the backend
    const validateUserGroup = async () => {
      try {
        const response = await API.post("/api/users/validate", {
          requiredGroup: requiredGroup,
        });

        if (
          response.data.validation === "false" ||
          response.data.validation === false
        ) {
          console.error(`User lacks permissions for group: ${requiredGroup}`);
          navigate("/unauthorized", { replace: true });
          return;
        }
      } catch (error) {
        if (error.response?.status === 403) {
          navigate("/unauthorized", { replace: true });
        } else if (error.response?.status !== 401) {
          console.error("Group validation error:", error);
          navigate("/unauthorized", { replace: true });
        }
      } finally {
        setIsValidating(false);
      }
    };

    validateUserGroup();
  }, [authLoading, user, requiredGroup, navigate]);

  if (!user && !authLoading) {
    return null;
  }

  // Pass routeLoading down to the wrapped child page component
  const routeLoading = authLoading || isValidating;

  return React.isValidElement(children)
    ? React.cloneElement(children, { routeLoading })
    : children;
};

export default PrivateRoute;
